import React, { useState, useEffect } from "react";
import {
  FolderOpen,
  Package,
  FileText,
  Shield,
  ChevronLeft,
  ChevronRight,
  Users,
  ShoppingCart,
  ClipboardList,
  Workflow,
  Database,
  BarChart3,
  Settings,
  Briefcase,
  UserCheck,
  CalendarDays,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
  UserPlus,
  CheckCircle,
  DollarSign,
  Webhook,
  Clock,
  Mail,
  Palette,
  Flag,
} from "lucide-react";
import { useNavigation, NavigationItem } from "../contexts/NavigationContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useAuth } from "../contexts/AuthContext";
import { AccountSwitcher } from "./AccountSwitcher";
import { APP_NAME } from "../constants/app";
import { useBranding } from "../contexts/BrandingContext";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileMenuOpen: boolean;
  onMobileMenuToggle: () => void;
}

const iconMap = {
  FolderOpen,
  Package,
  FileText,
  Shield,
  Users,
  ShoppingCart,
  ClipboardList,
  Workflow,
  Database,
  BarChart3,
  Settings,
  Briefcase,
  UserCheck,
  CalendarDays,
  LayoutDashboard,
  UserPlus,
  CheckCircle,
  DollarSign,
  Webhook,
  Clock,
  Mail,
  Palette,
  Flag,
};

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  mobileMenuOpen,
  onMobileMenuToggle,
}) => {
  const {
    navigationItems,
    navigate,
    filterNavigationByPermissions,
    currentPath,
  } = useNavigation();
  const {
    hasPermission,
    permissions,
    isLoading: permissionsLoading,
  } = usePermissions();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { config: brandingConfig } = useBranding();
  const isSystemAdmin = user?.role === "System Administrator";
  const [activeItem, setActiveItem] = useState("dashboard");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [filteredItems, setFilteredItems] =
    useState<NavigationItem[]>(navigationItems);

  // Filter navigation items based on permissions
  useEffect(() => {
    if (!permissionsLoading) {
      let filtered = filterNavigationByPermissions(
        navigationItems,
        hasPermission,
      );


      setFilteredItems(filtered);
    }
    // Note: Keep previous filteredItems visible while loading new permissions
  }, [
    navigationItems,
    permissions,
    permissionsLoading,
    filterNavigationByPermissions,
    hasPermission,
  ]);

  // Sync activeItem with current path on mount and path changes
  useEffect(() => {
    // Map current path to navigation item ID
    const findActiveItem = (
      items: NavigationItem[],
    ): { itemId: string | null; parentId: string | null } => {
      for (const item of items) {
        // Check children first (more specific matches)
        if (item.children) {
          for (const child of item.children) {
            if (
              child.path &&
              (currentPath === child.path ||
                currentPath.startsWith(child.path + "/"))
            ) {
              return { itemId: child.id, parentId: item.id };
            }
          }
        }
        // Check if current path matches this item's path
        if (item.path && currentPath === item.path) {
          return { itemId: item.id, parentId: null };
        }
        // Check if current path starts with this item's path (for sub-routes)
        if (
          item.path &&
          item.path !== "/" &&
          currentPath.startsWith(item.path)
        ) {
          return { itemId: item.id, parentId: null };
        }
      }
      return { itemId: null, parentId: null };
    };

    // Special case: root path or /dashboard should activate dashboard
    if (currentPath === "/" || currentPath === "/dashboard") {
      setActiveItem("dashboard");
      setExpandedItems([]);
      return;
    }

    // Special case: /admin routes should activate appropriate admin item
    if (currentPath.startsWith("/admin")) {
      const pathname = currentPath.split("?")[0]; // Remove query params
      const pathSegments = pathname.split("/").filter(Boolean);

      // Check for path-based routes like /admin/subscriptions
      if (pathSegments.length >= 2 && pathSegments[0] === "admin") {
        const pathSegment = pathSegments[1];
        if (pathSegment === "subscriptions") {
          setActiveItem("admin-subscriptions");
        } else if (pathSegment === "webhooks") {
          setActiveItem("admin-webhooks");
        } else if (pathSegment === "audit") {
          setActiveItem("admin-audit");
        } else if (pathSegment === "schedules") {
          setActiveItem("admin-schedules");
        } else if (pathSegment === "edm") {
          setActiveItem("admin-edm");
        } else if (pathSegment === "users") {
          setActiveItem("admin-users");
        } else {
          setActiveItem("admin");
        }
      } else {
        // Fallback to query parameter detection for backward compatibility
        const urlParams = new URLSearchParams(currentPath.split("?")[1] || "");
        const tab = urlParams.get("tab");
        if (tab === "plan-config") {
          setActiveItem("admin-subscriptions");
        } else if (tab === "webhooks") {
          setActiveItem("admin-webhooks");
        } else if (tab === "audit") {
          setActiveItem("admin-audit");
        } else if (tab === "schedules") {
          setActiveItem("admin-schedules");
        } else if (tab === "edm") {
          setActiveItem("admin-edm");
        } else if (tab === "users") {
          setActiveItem("admin-users");
        } else {
          setActiveItem("admin");
        }
      }
      setExpandedItems([]);
      return;
    }

    // Special case: /escrow should activate escrow
    if (currentPath.startsWith("/escrow")) {
      setActiveItem("escrow");
      setExpandedItems([]);
      return;
    }

    // Special case: /contacts
    if (currentPath.startsWith("/contacts")) {
      setActiveItem("contacts");
      setExpandedItems([]);
      return;
    }

    // Special case: /projects
    if (currentPath.startsWith("/projects")) {
      setActiveItem("projects");
      setExpandedItems([]);
      return;
    }

    const { itemId, parentId } = findActiveItem(navigationItems);
    if (itemId) {
      setActiveItem(itemId);
      // Expand parent if this is a child item
      if (parentId) {
        setExpandedItems((prev) =>
          prev.includes(parentId) ? prev : [...prev, parentId],
        );
      }
    }
  }, [currentPath, navigationItems]);

  const handleItemClick = (item: NavigationItem) => {
    setActiveItem(item.id);

    // Handle navigation
    if (item.path) {
      navigate(item.path);
    }

    // Handle expandable items
    if (item.children) {
      setExpandedItems((prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id],
      );
    }
  };

  const renderIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName as keyof typeof iconMap];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : null;
  };

  const handleItemClickWithClose = (item: NavigationItem) => {
    handleItemClick(item);
    // Close mobile menu after clicking
    if (mobileMenuOpen) {
      onMobileMenuToggle();
    }
  };

  // Build nav order: Dashboard, Projects, Contacts, then rest of filteredItems
  const topItems: NavigationItem[] = [
    { id: "projects", label: t("navigation.projects"), icon: "Briefcase", path: "/projects" },
    { id: "contacts", label: t("navigation.contacts"), icon: "UserCheck", path: "/contacts" },
  ];
  const dashboardItem = filteredItems.find((i) => i.id === "dashboard");
  const otherItems = filteredItems.filter(
    (i) => i.id !== "dashboard" && i.id !== "projects" && i.id !== "contacts"
  );
  const orderedNavItems: NavigationItem[] = [
    ...(dashboardItem ? [dashboardItem] : []),
    ...topItems,
    ...otherItems,
  ];

  return (
    <aside
      className={`
      bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 
      transition-all duration-300
      ${collapsed ? "w-20" : "w-64"}
      md:block
      fixed md:relative left-0 top-0 bottom-0 z-50 md:z-40
      transform md:transform-none transition-transform duration-300 ease-in-out
      ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
    `}
    >
      <div className="flex flex-col h-full pb-16 md:pb-0">
        {/* Toggle Button and Account Switcher - Desktop only */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 hidden md:block">
          {/* App Logo and Toggle */}
          <div
            className={`flex items-center ${collapsed ? "justify-center" : "justify-between mb-3"}`}
          >
            {collapsed ? (
              <button
                onClick={onToggle}
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer p-1"
                aria-label="Expand sidebar"
              >
                <img 
                  src={brandingConfig.logoUrl ? 
                    (brandingConfig.logoUrl.startsWith('http') ? brandingConfig.logoUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${brandingConfig.logoUrl}`) 
                    : "/logo.svg"
                  } 
                  alt={brandingConfig.appName} 
                  className="w-full h-full object-contain" 
                />
              </button>
            ) : (
              <>
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 p-1">
                    <img 
                      src={brandingConfig.logoUrl ? 
                        (brandingConfig.logoUrl.startsWith('http') ? brandingConfig.logoUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${brandingConfig.logoUrl}`) 
                        : "/logo.svg"
                      } 
                      alt={brandingConfig.appName} 
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {brandingConfig.appName}
                  </span>
                </div>
                <button
                  onClick={onToggle}
                  className="w-8 h-8 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0 ml-2"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          {!collapsed && <AccountSwitcher />}
        </div>

        {/* Mobile Header with Close Button */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 md:hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center p-1">
                <img 
                  src={brandingConfig.logoUrl ? 
                    (brandingConfig.logoUrl.startsWith('http') ? brandingConfig.logoUrl : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${brandingConfig.logoUrl}`) 
                    : "/logo.svg"
                  } 
                  alt={brandingConfig.appName} 
                  className="w-full h-full object-contain" 
                />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {brandingConfig.appName}
              </h2>
            </div>
            <button
              onClick={onMobileMenuToggle}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label={t("common.close")}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
          {/* Account Switcher - Mobile */}
          <AccountSwitcher />
        </div>

        {/* Navigation Items - Dashboard, Projects, Contacts at top */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" aria-label="Main navigation">
          {orderedNavItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => handleItemClickWithClose(item)}
                className={`w-full sidebar-item flex items-center p-3 ${
                  activeItem === item.id ? "active" : ""
                } ${collapsed && !mobileMenuOpen ? "justify-center" : "justify-between"}`}
                aria-label={
                  collapsed && !mobileMenuOpen ? item.label : undefined
                }
                title={collapsed && !mobileMenuOpen ? item.label : undefined}
              >
                <div
                  className={`flex items-center ${collapsed && !mobileMenuOpen ? "" : ""}`}
                >
                  {renderIcon(item.icon)}
                  {(!collapsed || mobileMenuOpen) && (
                    <span className="ml-3">
                      {t(`navigation.${item.id.toLowerCase()}`) || item.label}
                    </span>
                  )}
                </div>
                {item.children && (
                  <div className="flex items-center">
                    {expandedItems.includes(item.id) ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                )}
              </button>
              {item.children && expandedItems.includes(item.id) && (
                <div className="ml-4 mt-2 space-y-1">
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleItemClickWithClose(child)}
                      className={`w-full sidebar-item flex items-center p-2 ${
                        activeItem === child.id ? "active" : ""
                      } ${collapsed && !mobileMenuOpen ? "justify-center" : "justify-start"}`}
                      aria-label={
                        collapsed && !mobileMenuOpen ? child.label : undefined
                      }
                      title={
                        collapsed && !mobileMenuOpen ? child.label : undefined
                      }
                    >
                      {renderIcon(child.icon)}
                      {(!collapsed || mobileMenuOpen) && (
                        <span className="ml-3">
                          {t(`navigation.${child.id.toLowerCase()}`) ||
                            child.label}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Admin Section - Bottom (hidden for System Administrators) */}
        {/* Escrow tab hidden for future release */}
        {/* {!isSystemAdmin && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <button
              onClick={() =>
                handleItemClickWithClose({
                  id: "escrow",
                  label: t("navigation.escrow"),
                  icon: "DollarSign",
                  path: "/escrow",
                })
              }
              className={`w-full sidebar-item flex items-center p-3 ${
                activeItem === "escrow" ? "active" : ""
              } ${collapsed && !mobileMenuOpen ? "justify-center" : "justify-start"}`}
              aria-label={
                collapsed && !mobileMenuOpen
                  ? t("navigation.escrow")
                  : undefined
              }
              title={
                collapsed && !mobileMenuOpen
                  ? t("navigation.escrow")
                  : undefined
              }
            >
              <DollarSign className="w-6 h-6" />
              {(!collapsed || mobileMenuOpen) && (
                <span className="ml-3">{t("navigation.escrow")}</span>
              )}
            </button>
          </div>
        )} */}

        {/* Admin Section - Bottom */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={() =>
              handleItemClickWithClose({
                id: "admin",
                label: t("navigation.admin"),
                icon: "Settings",
                path: "/admin",
              })
            }
            className={`w-full sidebar-item flex items-center p-3 ${
              activeItem === "admin" ? "active" : ""
            } ${collapsed && !mobileMenuOpen ? "justify-center" : "justify-start"}`}
            aria-label={
              collapsed && !mobileMenuOpen ? t("navigation.admin") : undefined
            }
            title={
              collapsed && !mobileMenuOpen ? t("navigation.admin") : undefined
            }
          >
            <Settings className="w-6 h-6" />
            {(!collapsed || mobileMenuOpen) && (
              <span className="ml-3">{t("navigation.admin")}</span>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};
